import { nanoid } from 'nanoid'
import { z } from 'zod'

import type { RequestService } from '../request'

export function authApiFactory(requestService: RequestService) {
  /** Request a password reset for a given email */
  async function requestPasswordReset(email: string) {
    return await requestService.request({
      url: 'core/login/request-password-reset',
      method: 'post',
      data: {
        email,
        clientId: nanoid(),
      },
      schema: z.null(),
      disableAuth: true,
    })
  }

  /** Reset a password using an email-link token */
  async function resetPassword(
    password: string,
    clientId: string,
    resetToken: string,
  ) {
    return await requestService.request({
      url: 'core/login/reset-password',
      method: 'post',
      data: {
        clientId,
        resetToken,
        password,
      },
      disableAuth: true,
      schema: z.object({
        accessToken: z.string(),
        refreshToken: z.string(),
      }),
    })
  }

  async function generatePersonalToken({
    name,
    description,
    refreshToken,
  }: { name: string; description?: string; refreshToken: string }) {
    return await requestService.request({
      url: 'core/login/personal-token/generate',
      method: 'post',
      data: {
        name,
        description,
        refreshToken,
      },
      schema: z.object({
        uid: z.string(),
        personalToken: z.string(),
      }),
    })
  }

  async function getAccessTokenByPersonalToken({
    personalToken,
  }: { personalToken: string }) {
    return await requestService.request({
      url: 'core/login/personal-token/access',
      method: 'post',
      data: {
        personalToken,
      },
      schema: z.object({
        accessToken: z.string(),
      }),
    })
  }

  async function revokePersonalToken({ uid }: { uid: string }) {
    return await requestService.request({
      url: `core/login/personal-token/${uid}/revoke`,
      method: 'post',
      schema: z.null(),
    })
  }

  async function deletePersonalToken({ uid }: { uid: string }) {
    return await requestService.request({
      url: `core/login/personal-token/${uid}`,
      method: 'delete',
      schema: z.null(),
    })
  }

  return {
    requestPasswordReset,
    resetPassword,

    // Personal tokens
    generatePersonalToken,
    getAccessTokenByPersonalToken,
    revokePersonalToken,
    deletePersonalToken,
  }
}
